import {
  ProgramWizardCurrentProgram,
  ProgramWizardData,
  ProgramWizardOperatingMode,
  ProgramWizardStep,
  ProgramWizardStepValue,
} from './ProgramWizard.types';

type TranslateFn = (key: string) => string;

export const IDLE_PROGRAM_DESCRIPTION = 'Idle';

const isProgramActive = (currentProgram: ProgramWizardCurrentProgram | null | undefined): boolean =>
  !!currentProgram && currentProgram.description !== IDLE_PROGRAM_DESCRIPTION;

const EMPTY_WIZARD_DATA: Omit<ProgramWizardData, 'batteryId'> = {
  operatingMode: null,
  maxInputCurrent: null,
  genSetData: {
    1: null,
    2: null,
  },
  loadShareMasterType: null,
  shutDown24vEnabled: null,
};

export const getInitialWizardData = (batteryId: string): ProgramWizardData => ({
  ...EMPTY_WIZARD_DATA,
  batteryId,
});

// Only the shutdown program needs the device to also power down the 24V batteries.
export const getShutDown24vEnabled = (operatingMode: ProgramWizardOperatingMode | null): boolean | null =>
  operatingMode === ProgramWizardOperatingMode.SHUTDOWN ? true : null;

export const applyInputSettingsData = ({
  wizardData,
  data,
}: {
  wizardData: ProgramWizardData;
  data: { maxInputCurrent: string; genSetData1: string };
}): ProgramWizardData => ({
  ...wizardData,
  maxInputCurrent: parseFloat(data.maxInputCurrent),
  genSetData: {
    1: parseFloat(data.genSetData1) || null,
    2: null,
  },
});

const STEP_BEFORE_CONFIRM: Partial<Record<ProgramWizardOperatingMode, ProgramWizardStepValue>> = {
  [ProgramWizardOperatingMode.STANDALONE]: ProgramWizardStep.SELECT_PROGRAM,
  [ProgramWizardOperatingMode.STANDBY]: ProgramWizardStep.SELECT_PROGRAM,
  [ProgramWizardOperatingMode.SHUTDOWN]: ProgramWizardStep.SELECT_PROGRAM,
};

export const getStepBeforeConfirm = (operatingMode: ProgramWizardOperatingMode | null): ProgramWizardStepValue =>
  (operatingMode !== null && STEP_BEFORE_CONFIRM[operatingMode]) || ProgramWizardStep.INPUT_SETTINGS;

export const getTriggerButtonText = ({
  label,
  currentProgram,
  t,
}: {
  label: string | undefined;
  currentProgram: ProgramWizardCurrentProgram | null | undefined;
  t: TranslateFn;
}): string => label || (isProgramActive(currentProgram) ? t('changeProgram') : t('startProgram'));

export const getDialogTitle = ({
  step,
  currentProgram,
  t,
}: {
  step: ProgramWizardStepValue;
  currentProgram: ProgramWizardCurrentProgram | null | undefined;
  t: TranslateFn;
}): string => {
  if (step === ProgramWizardStep.STARTING) {
    return t('settingUpBattery');
  }
  return isProgramActive(currentProgram) ? t('program.title') : t('dialogTitle');
};

export const submitBatteryProgram = ({
  wizardData,
  onSubmit,
  onSuccess,
  onError,
}: {
  wizardData: ProgramWizardData;
  onSubmit: (data: ProgramWizardData) => Promise<string | void>;
  onSuccess: (message?: string | void) => void;
  onError: (error: unknown) => void;
}): void => {
  onSubmit(wizardData).then(onSuccess).catch(onError);
};
